import React, { useState, useEffect, useRef } from 'react'
import { Sparkles, Loader2, AlertTriangle, XCircle } from 'lucide-react'
import SidePanel from '@/components/ui/SidePanel'
import Button from '@/components/ui/Button'
import RichContent from '@/components/data/RichContent'
import MarkdownEditor from '@/components/ui/MarkdownEditor'
import { designTokens } from '@/utils/design-tokens'
import { TaskReportService } from '@/api/generated/services/TaskReportService'
import { JobsService } from '@/api/generated/services/JobsService'
import type { TaskWithReportResponse } from '@/api/generated/models/TaskWithReportResponse'
import type { ReportInfo } from '@/api/generated/models/ReportInfo'
import { useCanUseAI } from '@/hooks/useCanUseAI'
import { useAlert } from '@/components/ui/Alert/exports'
import {
  pollJobUntilDone,
  fetchFailedItemMessages,
  reportJobKey,
  rememberActiveJob,
  forgetActiveJob,
  getActiveJobId,
} from '@/utils/ai-jobs'

// 템플릿 키 → 필드 키 → 보고서 작성 화면 칸 이름 (작성 방식 표시)
const FIELD_LABELS: Record<string, Record<string, string>> = {
  // 자체평가(기관평가인증 2식) — AI 분석은 '현황 및 분석 결과' 한 칸만
  self_evaluation_org_judgment: {
    evaluation_criterion: '평가준거(직접입력)',
    formula: '산출식(직접입력)',
    prev_improve_plan: '전년도 개선 계획(직접입력)',
    calc_value: '산출값(직접입력)',
    evaluation_result: '현황 및 분석결과(AI분석)',
  },
}

interface ReportWritePanelProps {
  isOpen: boolean
  onClose: () => void
  selectedReport: TaskWithReportResponse | null
  onSuccess?: () => void
}

// AI 생성 일시 포맷 (YYYY.MM.DD HH:mm)
const formatAiDate = (dateString: string): string => {
  try {
    return new Date(dateString).toLocaleString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
    })
  } catch {
    return dateString
  }
}

// Reusable Section Header Component
const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div
    className="pb-2 border-b"
    style={{
      borderBottomColor: '#EBEBEB',
      borderBottomWidth: '1px',
      padding: '7px 0px'
    }}
  >
    <h3
      style={{
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: '18px',
        fontWeight: 700,
        color: designTokens.colors.gray[6]
      }}
    >
      {title}
    </h3>
  </div>
)

// Reusable Info Field Component (Read-only)
const InfoField: React.FC<{
  label: string
  value: string | React.ReactNode
}> = ({ label, value }) => (
  <div className="flex items-center gap-5">
    <div className="w-37 flex-shrink-0">
      <span
        style={{
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: '16px',
          fontWeight: 400,
          color: designTokens.colors.gray[6]
        }}
      >
        {label}
      </span>
    </div>
    <div className="flex-1 py-2">
      {typeof value === 'string' ? (
        <span
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '16px',
            fontWeight: 400,
            color: designTokens.colors.gray[6]
          }}
        >
          {value}
        </span>
      ) : (
        value
      )}
    </div>
  </div>
)

const ReportWritePanel: React.FC<ReportWritePanelProps> = ({
  isOpen,
  onClose,
  selectedReport,
  onSuccess
}) => {
  // 워크스페이스 AI 설정 + 내 역할 기반 AI UI 노출 여부
  const canUseAI = useCanUseAI()
  const { showInfo } = useAlert()

  // Form state for dynamic fields
  const [formData, setFormData] = useState<Record<string, string>>({})

  // AI 생성 값 (field_key → ai_value), 생성 일시
  const [aiValues, setAiValues] = useState<Record<string, string>>({})
  const [aiGeneratedAt, setAiGeneratedAt] = useState<Record<string, string>>({})

  // Report status
  const [status, setStatus] = useState<'incomplete' | 'completed'>('incomplete')

  // Loading and error states
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // 패널이 닫히면 폴링을 중단하기 위한 플래그 (서버 측 분석은 계속 진행됨)
  const analysisCancelledRef = useRef(false)

  // 추적 중인 분석 작업 id — 있어야 "분석 취소" 버튼을 노출/동작시킬 수 있다
  const [activeJobId, setActiveJobId] = useState<string | null>(null)
  const [isCancelingJob, setIsCancelingJob] = useState(false)
  // 이 패널에서 사용자가 직접 취소를 눌렀는지 — 취소 종료 메시지를 에러가 아닌 안내로 보여주기 위함
  const userCancelRequestedRef = useRef(false)

  // 패널 내에서 새로 생성한 보고서 id (보고서가 없던 상태에서 AI 분석 등으로 생성된 경우 추적 → 중복 생성 방지)
  const [localReportId, setLocalReportId] = useState<string | null>(null)

  // Initialize form data when panel opens
  useEffect(() => {
    if (!isOpen || !selectedReport) return

    // Initialize status
    const initialStatus = selectedReport.report?.status || 'incomplete'
    setStatus(initialStatus as 'incomplete' | 'completed')

    // Initialize form data + AI values from existing report infos
    if (selectedReport.report?.infos) {
      const initialData: Record<string, string> = {}
      const aiData: Record<string, string> = {}
      const aiAt: Record<string, string> = {}
      selectedReport.report.infos.forEach(info => {
        initialData[info.template_field_key] = info.value
        if (info.ai_value) {
          aiData[info.template_field_key] = info.ai_value
          if (info.ai_generated_at) aiAt[info.template_field_key] = info.ai_generated_at
        }
      })
      setFormData(initialData)
      setAiValues(aiData)
      setAiGeneratedAt(aiAt)
    } else {
      // Clear form for new report
      setFormData({})
      setAiValues({})
      setAiGeneratedAt({})
    }

    // Clear error + 새로 생성 추적값 초기화
    setError(null)
    setLocalReportId(null)
  }, [isOpen, selectedReport])

  // 패널 닫힘/언마운트 시 진행 중인 분석 폴링 중단 (서버 분석은 계속 진행 — 재진입 시 아래에서 추적 재개)
  useEffect(() => {
    if (!isOpen) {
      analysisCancelledRef.current = true
      setIsAnalyzing(false)
    }
    return () => {
      analysisCancelledRef.current = true
    }
  }, [isOpen])

  // Validate form
  const validateForm = (): boolean => {
    if (!selectedReport?.report_template?.fields) {
      setError('보고서 템플릿 정보가 없습니다.')
      return false
    }

    // Check required fields
    const requiredFields = selectedReport.report_template.fields.filter(f => f.required)

    for (const field of requiredFields) {
      if (!formData[field.key]?.trim()) {
        setError(`${field.name}은(는) 필수 입력 항목입니다.`)
        return false
      }
    }

    setError(null)
    return true
  }

  // 현재 입력값(빈 값 제외)을 ReportInfo 배열로 변환
  const buildInfos = (): ReportInfo[] =>
    Object.entries(formData)
      .filter(([_, value]) => value.trim() !== '')
      .map(([key, value]) => ({ template_field_key: key, value: value.trim() }))

  // 보고서 id 확보 — 이미 있으면 그대로, 없으면 현재 입력값으로 생성(필수검증 없음)하고 id 반환.
  // 생성한 id는 localReportId에 보관해 이후 저장/분석이 중복 생성하지 않도록 한다.
  const ensureReportId = async (): Promise<string | null> => {
    const existing = selectedReport?.report?.id ?? localReportId
    if (existing) return existing

    if (!selectedReport?.report_template?.key) {
      setError('보고서 템플릿 정보가 없습니다.')
      return null
    }

    const infos = buildInfos()
    const created = await TaskReportService.appsTaskViewsCreateTaskReportEndpoint({
      requestBody: {
        task_id: selectedReport.task.id,
        report_template_key: selectedReport.report_template.key,
        status,
        infos: infos.length > 0 ? infos : null,
      },
    })
    const newId = created?.id ?? null
    if (newId) setLocalReportId(newId)
    return newId
  }

  // Handle submit
  const handleSubmit = async () => {
    if (!selectedReport) return

    // Validate
    if (!validateForm()) return

    setIsSubmitting(true)
    setError(null)

    try {
      const infos = buildInfos()
      const effectiveReportId = selectedReport.report?.id ?? localReportId

      if (effectiveReportId) {
        // UPDATE existing report (패널 내 생성분 포함)
        await TaskReportService.appsTaskViewsUpdateTaskReportEndpoint({
          reportId: effectiveReportId,
          requestBody: {
            status,
            infos: infos.length > 0 ? infos : null
          }
        })
      } else {
        // CREATE new report
        if (!selectedReport.report_template?.key) {
          setError('보고서 템플릿 정보가 없습니다.')
          return
        }

        await TaskReportService.appsTaskViewsCreateTaskReportEndpoint({
          requestBody: {
            task_id: selectedReport.task.id,
            report_template_key: selectedReport.report_template.key,
            status,
            infos: infos.length > 0 ? infos : null
          }
        })
      }

      // Success
      onSuccess?.()
      onClose()
    } catch (err: any) {
      console.error('Failed to save report:', err)

      // Handle specific error codes
      if (err.status === 400) {
        setError('잘못된 요청입니다. 입력 내용을 확인해주세요.')
      } else if (err.status === 404) {
        setError('보고서를 찾을 수 없습니다.')
      } else {
        setError('보고서 저장에 실패했습니다.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  // 완료된 분석 결과 반영 — 서버가 ai_value/ai_generated_at을 저장하므로 상세 재조회가 정확하다
  const refreshAiValuesFromServer = async () => {
    if (!selectedReport?.task?.id || !selectedReport.report_template?.key) return

    const fresh = await TaskReportService.appsTaskViewsGetTaskReportDetail({
      taskId: selectedReport.task.id,
      reportTemplateId: selectedReport.report_template.key,
    })

    const aiData: Record<string, string> = {}
    const aiAt: Record<string, string> = {}
    fresh.report?.infos?.forEach(info => {
      if (info.ai_value) {
        aiData[info.template_field_key] = info.ai_value
        if (info.ai_generated_at) aiAt[info.template_field_key] = info.ai_generated_at
      }
    })
    setAiValues(aiData)
    setAiGeneratedAt(aiAt)
  }

  // 실패 항목의 필드명·오류 메시지 수집 (field_key → 템플릿 필드명 변환)
  const collectFailureDetail = async (jobId: string): Promise<string> => {
    try {
      const failures = await fetchFailedItemMessages(jobId)
      const fieldNameByKey = new Map(
        (selectedReport?.report_template?.fields ?? []).map(f => [f.key, f.name])
      )
      return failures
        .map(f =>
          f.fieldKey
            ? `· ${fieldNameByKey.get(f.fieldKey) ?? f.fieldKey}: ${f.message}`
            : `· ${f.message}`
        )
        .join('\n')
    } catch (err) {
      console.error('Failed to fetch failed job items:', err)
      return ''
    }
  }

  // 등록된 분석 작업을 종료 상태까지 추적하고 결과를 반영한다.
  // (최초 실행과 패널 재진입 복원이 같은 경로를 쓰도록 분리)
  const trackAnalysisJob = async (jobId: string, reportId: string) => {
    setActiveJobId(jobId)
    // 종료 상태(completed/completed_with_errors/failed/canceled)까지 폴링
    const job = await pollJobUntilDone(jobId, {
      isCancelled: () => analysisCancelledRef.current,
    })
    if (!job) return // 패널이 닫혀 폴링 중단 (서버 분석은 계속됨 — 재진입 시 다시 추적)

    // 종료됐으므로 추적 대상에서 제거
    setActiveJobId(null)
    forgetActiveJob(reportJobKey(reportId))

    if (job.status === 'canceled') {
      if (userCancelRequestedRef.current) {
        userCancelRequestedRef.current = false
        showInfo('AI 분석을 취소했습니다. 이미 완료된 필드의 결과는 남아 있을 수 있습니다.')
        await refreshAiValuesFromServer() // 취소 전에 끝난 필드의 ai_value 반영
      } else {
        setError('AI 분석 작업이 취소되었습니다.')
      }
      return
    }
    if (job.status === 'failed') {
      const failures = await collectFailureDetail(job.id)
      setError(failures ? `AI 분석에 실패했습니다.\n${failures}` : 'AI 분석에 실패했습니다.')
      return
    }

    // completed / completed_with_errors — 저장된 결과를 재조회해 "AI 생성 값" 박스에 반영
    await refreshAiValuesFromServer()

    if (job.status === 'completed_with_errors') {
      const failures = await collectFailureDetail(job.id)
      setError(
        failures
          ? `일부 필드의 AI 분석에 실패했습니다.\n${failures}`
          : '일부 필드의 AI 분석에 실패했습니다.'
      )
    }
  }

  // AI 분석 취소 — 대기 중인 필드는 즉시 취소되고, 이미 SLM에 들어간 호출은
  // 현재 필드까지 처리한 뒤 중단된다(백엔드 계약). 종료 감지는 기존 폴링이 맡으므로
  // 여기서는 취소 요청만 보낸다.
  const handleCancelAnalysis = async () => {
    if (!activeJobId || isCancelingJob) return
    setIsCancelingJob(true)
    userCancelRequestedRef.current = true
    try {
      await JobsService.appsJobsViewsCancelJobEndpoint({ jobId: activeJobId })
      // 성공 시 폴링이 canceled 종료를 감지해 위 trackAnalysisJob 분기가 마무리한다
    } catch (err: any) {
      userCancelRequestedRef.current = false
      // 이미 종료된 작업(400대) 등 — 폴링이 실제 종료 상태로 마무리하므로 안내만
      console.error('Failed to cancel AI analysis job:', err)
      if (err?.status === 403) {
        setError('작업을 취소할 권한이 없습니다. (요청자 본인 또는 시스템 관리자만 가능)')
      }
    } finally {
      setIsCancelingJob(false)
    }
  }

  // AI 분석 — 작업(Job)을 등록하고 종료 상태까지 폴링한 뒤, 저장된 ai_value를 재조회해 반영한다.
  // 보고서가 아직 없으면 먼저 생성한 뒤 분석한다.
  const handleAiAnalysis = async () => {
    if (!selectedReport) return

    setIsAnalyzing(true)
    setError(null)
    analysisCancelledRef.current = false
    userCancelRequestedRef.current = false

    try {
      const reportId = await ensureReportId()
      if (!reportId) return // ensureReportId가 에러 메시지 설정

      // 1) 분석 작업 등록 — 즉시 202 반환, 실제 분석은 백그라운드 워커가 수행
      const created = await JobsService.appsJobsViewsCreateAiAnalysisJob({
        requestBody: { scope: 'report', report_id: reportId },
      })

      if (created.existing) {
        showInfo('이미 진행 중인 분석 작업이 있어 해당 작업의 완료를 기다립니다.')
      }

      // 패널을 닫았다 다시 열어도 같은 작업을 이어서 추적하도록 기억해 둔다
      rememberActiveJob(reportJobKey(reportId), created.job_id)

      // 2) 종료까지 추적 + 결과 반영
      await trackAnalysisJob(created.job_id, reportId)
    } catch (err: any) {
      console.error('AI analysis failed:', err)
      let baseMessage: string
      if (err?.status === 400) {
        baseMessage = 'AI 분석 요청이 올바르지 않습니다.'
      } else if (err?.status === 404) {
        baseMessage = '보고서를 찾을 수 없습니다.'
      } else {
        baseMessage = 'AI 분석에 실패했습니다.'
      }

      // API 응답의 error 필드 — 없거나 빈 값이면 안전하게 생략 (파싱 오류 방지)
      const detail = typeof err?.body?.error === 'string' ? err.body.error.trim() : ''
      setError(detail ? `${baseMessage}\n${detail}` : baseMessage)
    } finally {
      if (!analysisCancelledRef.current) {
        setIsAnalyzing(false)
      }
    }
  }

  // 패널 재진입 시 진행 중이던 분석 작업 복원.
  // 분석은 서버에서 계속되므로, 기억해 둔 작업을 다시 추적해 진행 표시와
  // 버튼 잠금(중복 요청 방지)을 이어간다.
  const restoredJobIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (!isOpen) {
      restoredJobIdRef.current = null
      return
    }

    const reportId = selectedReport?.report?.id ?? localReportId
    if (!reportId) return

    const jobId = getActiveJobId(reportJobKey(reportId))
    // 이미 추적 중이면(분석 실행 직후 재렌더 등) 중복 폴링하지 않는다
    if (!jobId || isAnalyzing || restoredJobIdRef.current === jobId) return

    restoredJobIdRef.current = jobId
    analysisCancelledRef.current = false
    setIsAnalyzing(true)

    void (async () => {
      try {
        await trackAnalysisJob(jobId, reportId)
      } catch (err) {
        // 작업을 더 이상 조회할 수 없으면(삭제/권한 등) 추적을 포기한다
        console.error('Failed to restore AI analysis job:', err)
        forgetActiveJob(reportJobKey(reportId))
      } finally {
        if (!analysisCancelledRef.current) {
          setIsAnalyzing(false)
        }
      }
    })()
    // trackAnalysisJob은 매 렌더 새로 만들어지므로 의존성에서 제외한다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedReport?.report?.id, localReportId])

  // Header actions
  const headerActions = (
    <>
      <Button
        size="small"
        color="white"
        onClick={onClose}
        disabled={isSubmitting}
      >
        취소
      </Button>
      <Button
        size="small"
        color="dark"
        onClick={handleSubmit}
        disabled={isSubmitting}
      >
        {isSubmitting ? '저장 중...' : '저장'}
      </Button>
    </>
  )

  // Sort fields by sort_key
  const sortedFields = selectedReport?.report_template?.fields
    ? [...selectedReport.report_template.fields].sort((a, b) => a.sort_key - b.sort_key)
    : []

  // 보고서 작성 칸 이름 — 작성 방식을 붙여 보여 줄 칸만 정해 둔다(발주사 요청 문구 그대로).
  // 여기 없는 템플릿·필드는 백엔드 필드명 그대로.
  const fieldLabel = (field: (typeof sortedFields)[number]): string =>
    FIELD_LABELS[selectedReport?.report_template?.key ?? '']?.[field.key] ?? field.name

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      title={selectedReport?.report?.id ? '보고서 수정' : '보고서 작성'}
      width={800}
      headerActions={headerActions}
    >
      <div className="p-6 space-y-6">
        {selectedReport && (
          <>
            {/* Section 1: Basic Info */}
            <div className="space-y-4">
              <SectionHeader title="기본 정보" />

              {/* 세부실행과제명 (read-only) */}
              <InfoField
                label="세부실행과제명"
                value={selectedReport.task.name || '-'}
              />

              {/* 보고서 유형 (read-only) */}
              <InfoField
                label="보고서 유형"
                value={selectedReport.report_template?.name || '-'}
              />

              {/* 보고서 상태 (editable) */}
              <div className="flex items-center gap-5">
                <div className="w-37 flex-shrink-0">
                  <span
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '16px',
                      fontWeight: 400,
                      color: designTokens.colors.gray[6]
                    }}
                  >
                    보고서 상태
                  </span>
                </div>
                <div className="flex gap-3">
                  <Button
                    size="small"
                    color={status === 'incomplete' ? 'dark' : 'light'}
                    onClick={() => setStatus('incomplete')}
                    disabled={isSubmitting}
                  >
                    미완료
                  </Button>
                  <Button
                    size="small"
                    color={status === 'completed' ? 'dark' : 'light'}
                    onClick={() => setStatus('completed')}
                    disabled={isSubmitting}
                  >
                    완료
                  </Button>
                </div>
              </div>
            </div>

            {/* Section 2: Dynamic Fields */}
            {sortedFields.length > 0 && (
              <div className="space-y-4">
                {/* 섹션 헤더 + AI 분석 버튼 */}
                <div
                  className="pb-2 border-b flex items-center justify-between gap-2"
                  style={{
                    borderBottomColor: '#EBEBEB',
                    borderBottomWidth: '1px',
                    padding: '7px 0px',
                  }}
                >
                  <h3
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '18px',
                      fontWeight: 700,
                      color: designTokens.colors.gray[6],
                    }}
                  >
                    보고서 내용
                  </h3>
                  {canUseAI && (
                    <div className="flex items-center gap-2">
                      {isAnalyzing && activeJobId && (
                        <Button
                          size="small"
                          color="light"
                          onClick={handleCancelAnalysis}
                          disabled={isCancelingJob}
                          className="flex items-center gap-1.5"
                        >
                          <XCircle className="w-4 h-4" strokeWidth={2} />
                          {isCancelingJob ? '취소 중...' : '분석 취소'}
                        </Button>
                      )}
                      <Button
                        size="small"
                        color="dark"
                        onClick={handleAiAnalysis}
                        disabled={isSubmitting || isAnalyzing}
                        className="flex items-center gap-1.5"
                      >
                        {isAnalyzing ? (
                          <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
                        ) : (
                          <Sparkles className="w-4 h-4" strokeWidth={2} />
                        )}
                        {isAnalyzing ? 'AI 분석 중...' : 'AI 분석'}
                      </Button>
                    </div>
                  )}
                </div>

                {/* AI 분석 주의 문구 — AI 분석 버튼이 노출되는 동안 항상 표시 */}
                {canUseAI && (
                  <div
                    className="flex items-start gap-2.5 rounded p-3"
                    style={{
                      backgroundColor: designTokens.colors.error.light,
                      border: `1px solid ${designTokens.colors.error.DEFAULT}`,
                    }}
                  >
                    <AlertTriangle
                      className="w-5 h-5 flex-shrink-0 mt-0.5"
                      strokeWidth={2}
                      style={{ color: designTokens.colors.error.DEFAULT }}
                    />
                    <div className="flex flex-col gap-1">
                      <span
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: '14px',
                          fontWeight: 600,
                          color: designTokens.colors.gray[6],
                        }}
                      >
                        AI 분석은 오차가 발생할 수 있으므로 참고용 초안으로만 활용해 주시기 바랍니다.
                      </span>
                      <span
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: '14px',
                          fontWeight: 700,
                          color: designTokens.colors.error.DEFAULT,
                        }}
                      >
                        사용 전 반드시 직접 내용을 검토 및 확정해 주세요.
                      </span>
                    </div>
                  </div>
                )}

                {/* AI 분석 진행 인디케이터 — 응답이 오래 걸릴 수 있어 명확히 표시 */}
                {isAnalyzing && (
                  <div
                    className="flex items-center gap-3 rounded p-3"
                    style={{
                      backgroundColor: designTokens.colors.secondary.light,
                      border: `1px solid ${designTokens.colors.secondary.DEFAULT}`,
                    }}
                  >
                    <Loader2
                      className="w-5 h-5 animate-spin flex-shrink-0"
                      strokeWidth={2}
                      style={{ color: designTokens.colors.secondary.dark }}
                    />
                    <div className="flex flex-col">
                      <span
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: '14px',
                          fontWeight: 600,
                          color: designTokens.colors.secondary.dark,
                        }}
                      >
                        AI가 보고서를 분석하고 있습니다.
                      </span>
                      <span
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: '12px',
                          color: designTokens.colors.gray[5],
                        }}
                      >
                        완료까지 몇 분 정도 걸릴 수 있어요. 분석은 서버에서 진행되므로 창을 닫아도 계속됩니다.
                      </span>
                    </div>
                  </div>
                )}

                {sortedFields.map(field => (
                  <div key={field.key} className="flex items-start gap-5">
                    <div className="w-37 flex-shrink-0 pt-2">
                      <span
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: '16px',
                          fontWeight: 400,
                          color: designTokens.colors.gray[6]
                        }}
                      >
                        {fieldLabel(field)}
                        {field.required && (
                          <span style={{ color: designTokens.colors.error.DEFAULT }}> *</span>
                        )}
                      </span>
                      {field.description && (
                        <div
                          style={{
                            fontSize: '12px',
                            color: designTokens.colors.gray[4],
                            marginTop: '4px'
                          }}
                        >
                          {field.description}
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      {/* WYSIWYG 에디터 — 화면에 보이는 서식 그대로 편집, 값은 markdown 으로 저장 */}
                      <MarkdownEditor
                        value={formData[field.key] || ''}
                        onChange={(markdown) =>
                          setFormData(prev => ({ ...prev, [field.key]: markdown }))
                        }
                        height="auto"
                        placeholder={`${field.name}을(를) 입력하세요`}
                        disabled={isSubmitting}
                      />

                      {/* AI 생성 값 — 있으면 보여주고, 클릭(적용) 시 입력값으로 반영 */}
                      {aiValues[field.key] && (
                        <div
                          className="mt-2 rounded border p-3"
                          style={{
                            borderColor: designTokens.colors.secondary.DEFAULT,
                            backgroundColor: designTokens.colors.gray[0],
                          }}
                        >
                          <div className="flex items-center justify-between gap-2 mb-1.5">
                            <span
                              style={{
                                fontFamily: designTokens.typography.fontFamily.sans[0],
                                fontSize: '12px',
                                fontWeight: 600,
                                color: designTokens.colors.secondary.dark,
                              }}
                            >
                              AI 생성 값{aiGeneratedAt[field.key] ? ` · ${formatAiDate(aiGeneratedAt[field.key])}` : ''}
                            </span>
                            <Button
                              size="xsmall"
                              color="light"
                              onClick={() =>
                                setFormData(prev => ({ ...prev, [field.key]: aiValues[field.key] }))
                              }
                              disabled={isSubmitting || formData[field.key] === aiValues[field.key]}
                            >
                              {formData[field.key] === aiValues[field.key] ? '적용됨' : '적용'}
                            </Button>
                          </div>
                          <RichContent content={aiValues[field.key]} />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div
                className="p-4 rounded"
                style={{
                  backgroundColor: designTokens.colors.error.light,
                  border: `1px solid ${designTokens.colors.error.DEFAULT}`
                }}
              >
                <p
                  style={{
                    color: designTokens.colors.error.dark,
                    fontSize: '14px',
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    whiteSpace: 'pre-line'
                  }}
                >
                  {error}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </SidePanel>
  )
}

export default ReportWritePanel
